/*globals Sentry:false */

import AdvancedSearchM from '../model/recordm.model.advancedsearch.js';
import BulkRequestM from '../model/recordm.model.bulkrequest.js';
import { DEV_MODE } from '@cob/ui-genesis/helpers/cob.devmode.js';
import DuplicateFieldsPresenter from '../presenter/recordm.presenter.instance.duplicatefield.js';
import FieldPresenter from '../presenter/recordm.presenter.instance.field.js';
import InstanceM from '../model/recordm.model.entity.instance.js';
import InstanceV from '../view/recordm.view.instance.js';
import { Keywords } from '../fields/recordm.fields.js';
import PubSub from '@cob/ui-genesis/helpers/cob.pubsub.js';
import SimpleSearchM from '../model/recordm.model.simplesearch.js';
import utils from '@cob/ui-genesis/cob.utils.js';

// args:
// * `core`: cob.app
// * `$container`: The html container
// * `emptyInstanceM`: A empty instance of a definition
// * `clipboardP`: the clipboard presenter
// * `callbacks`: this Presenter accepts 2 callbacks:
//       * `cancel`: called when the user cancels the edit
//       * `afterSave`: called after the request is submited
// * `opts`: accepts:
//       * `exitLabel`: a localize key to be used on the exit (back) button
var GroupEditInstanceP = function(core, $container, emptyInstanceM, clipboardP, callbacks, opts) {

    const internal = new PubSub();

    var _searchM;

    var _definitionId,
        _query,
        _ids;

    var _fieldPs = [],
        _dupPs = [],
        _opts = {
            edit: function(field) {
                return _canEditField(field);
            },
            mandatory: false,
            validate: false,

            groupEditMode: true,
            enforceEmptyValue: true
        };

    utils.extend(_opts, opts, true);

    const saveBehaviorsM =  core.getSaveBehaviorsM(emptyInstanceM.getDefinitionName());

    const _hasFieldsChanged = function (fieldPs) {
        for (let i = 0; i < fieldPs.length; i++) {
            if (_fieldPs[i].hasValueChanged()){
                return true
            }
        }

        return false
    }

    var view = new InstanceV(core, $container, {
            canEdit: true,
            exitLabel: _opts.exitLabel,
            saveBehaviors: saveBehaviorsM.getDefaultBehaviors(),
            dataLossProtectionEnabled: core.getGroups()?.indexOf("FEAT Beta Adopter") !== -1
        },
        {
            collapseAll: () => internal.publish('collapse-all'),
            expandAll: () => internal.publish('expand-all'),
            exit: callbacks.cancel,
            hasDataChanged: () => _hasFieldsChanged(_fieldPs),
            save: function(behaviorName) {
                new BulkRequestM(core).updateInstances(
                    _definitionId,
                    _query && _searchM ? _searchM.getCurrentQuery() : undefined,
                    _ids ? _ids.split(",") : undefined,
                    _getModel()
                );

                view.stopDataLossProtectionListener()

                const afterSaveCb = saveBehaviorsM.getActionFor(behaviorName, callbacks);

                if(typeof afterSaveCb === "function"){
                    afterSaveCb();
                }
            }
        }
    );
    view.init();

    // Check if the field has the keyword $groupEdit in order to be editable,
    // and in case the field also has $editForGroup check if the user belongs to the group.
    var _canEditField = function(field) {
        const _groupEditField = field.fieldDefinition.isA(Keywords.GroupEdit);

        return  _groupEditField &&
                InstanceM.canEditField(core, field, emptyInstanceM.jsonDefinition);
    };

    var _getModel = function() {
        var instanceData = emptyInstanceM.data,
            instanceModel = {};

        instanceModel.jsonDefinition = instanceData.jsonDefinition;
        instanceModel.fields = [];

        // We want to build the model and remove
        // all the non $groupEdit fields

        _fieldPs.forEach(function(el) {
            instanceModel.fields.push(el.getModel(false));
        });

        if (_dupPs) {
            _dupPs.forEach(function(el) {
                el.fillModel(instanceModel, false);
            });
        }

        emptyInstanceM.data = instanceModel;
        emptyInstanceM.keepOnlyFieldsThat(function(field) {
            return _canEditField(field) && field.value;
        });

        return instanceModel;
    };

    var _countInstancesAffected = function(definitionId, query, ids, callback) {

        var isAdvSearch = false,
            advQuery;

        if (query) {

            try {
                advQuery = JSON.parse(query);

                // JSON.parse(10) returns 10 and will not throw any exception
                isAdvSearch = (typeof advQuery === 'object');

            } catch (e) {
                DEV_MODE && window.console.log("INFO: Conversion to json failed so I will handle it has a query string", query);
            }

            if (isAdvSearch) {
                _searchM = new AdvancedSearchM(
                    core,
                    "recordm/definitions/search/advanced/" + definitionId
                );
            } else {
                _searchM = new SimpleSearchM(
                    core,
                    "recordm/definitions/search/" + definitionId
                );
            }

            _searchM.setRowsPerPage(0);
            _searchM.setCurrentQuery(!isAdvSearch ? query : advQuery);
            _searchM._executeSearch({onSearchDone: function() {
                callback(_searchM.getTotalMatchingResults());
            }});

        } else {
            callback(ids==="" ? 0 : ids.split(",").length);
        }
    };

    var _init = function(query, ids) {
        _definitionId = emptyInstanceM.getDefinitionId();
        _query = query;
        _ids = ids;

        _countInstancesAffected(_definitionId, _query, _ids, function(totalAffected) {

            var instanceData = emptyInstanceM.data;

            _fieldPs = [];
            _dupPs = [];

            view.showInstanceForEditInGroup(instanceData, totalAffected);

            if (instanceData.fields) {
                instanceData.fields.map(function(instanceField) {
                    if (instanceField.fieldDefinition.duplicable) {

                        if (instanceField.duplicate) {
                            _dupPs[instanceField.fieldDefinition.id].addDuplicate(null, instanceField, true);

                        } else {
                            _dupPs[instanceField.fieldDefinition.id] = new DuplicateFieldsPresenter(
                                core, internal, view, instanceData, instanceField, {
                                    id: instanceData.id,
                                    isField: false,
                                   // used by $auto fields to find target fields outside their ancestor tree
                                   findSibling: function(name){
                                      return _findFieldPsIn(_fieldPs.concat(_dupPs), name );
                                   }
                                }, _opts, true, clipboardP, {}
                            );
                        }
                    } else {

                        var newFieldP = new FieldPresenter(
                            core, internal, instanceData, instanceField, {
                                isField: false,
                               // used by $auto fields to find target fields outside their ancestor tree
                               findSibling: function(name){
                                  return _findFieldPsIn(_fieldPs.concat(_dupPs), name );
                               }
                            }, _opts, true, clipboardP, {}
                        );

                        _fieldPs.push(newFieldP);
                        view.addField(newFieldP);
                    }
                });
            }

            // We publish 2 instance-ready events so that we can securely unsubscribe all subscribers of the internal one in
            // the end of this presenter and we can let the main version for public subscription usage (like we may
            // do in customizations.js).
            // This approach is kind of an hack because we could't get rid of all the subscriptions made in this presenter
            // and there were subscribers being left in the bus (and we could not just unsubscribe everything beacause of the
            // customizations.js being wrongly unsubscribed).
            core.publish("instance-ready");
            internal.publish("instance-ready-internal");

            view.startDataLossProtectionListener();

            /* eslint-disable-next-line no-invalid-this  */
            core.applyCustomizations(emptyInstanceM.getDefinitionName(), emptyInstanceM, {
                findFieldPs: function(predicate){
                    return _findFieldPsIn(_fieldPs.concat(_dupPs), predicate)
                },

                findFieldPsUnder: function(fieldP, predicate){
                    const allChildren = fieldP.getChildPs().concat(fieldP.getChildDuPs());
                    return _findFieldPsIn(allChildren, predicate);
                },

                onFieldChange: function(field, listener){
                    if(typeof field === 'string'){
                    if((/%[0-9a-zA-Z]{2}/).test(field)){
                       window.console.warn('onFieldChange called with already encoded field name, please remove it.')
                       if(typeof Sentry !== 'undefined') Sentry.captureMessage(
                          'onFieldChange called with already encoded field name, please remove it.'
                       );

                    } else {
                       field = encodeURIComponent(field);
                    }
                        core.instanceChangeListener(emptyInstanceM.getDefinitionName(), 'field-value-change-byName-' + field, listener);

                    } else if(typeof field.getChildPs !== 'undefined') {
                        // it's a field presenter
                        core.instanceChangeListener(emptyInstanceM.getDefinitionName(), 'field-value-change-' + field.getId(), listener);

                    } else if(typeof field.fieldDefinition !== 'undefined') {
                        // it's an instance field
                        core.instanceChangeListener(emptyInstanceM.getDefinitionName(), 'field-value-change-' + field.id, listener);

                    } else {
                        window.console.warn("don't know what to do with this", field)
                    }
                },

                isGroupEdit: function() {
                    return true;
                }
            });

        });
    };

    const _findFieldPs = function(fieldAndDupPs, predicate){
        let _matched = [];

        fieldAndDupPs.forEach((p) => {

            if(typeof p.addDuplicate === 'undefined'){
                // regular FieldPresenter
                if(predicate(p)) _matched.push(p);

                if(p.getChildPs()) _matched = _matched.concat(_findFieldPs(p.getChildPs(), predicate));
                if(p.getChildDuPs()) _matched = _matched.concat(_findFieldPs(p.getChildDuPs(), predicate))

            } else {
                // a DupFieldPresenter
                if(p.getFieldPs()) _matched = _matched.concat(_findFieldPs(p.getFieldPs(), predicate))
            }
        });

        return _matched;
    };

    const _findFieldPsIn = function(children, predicate){
        if(typeof predicate === 'string'){
            return _findFieldPs(children, function(fp){
                return  fp.isVisible() && fp.field.fieldDefinition.name === predicate
            })
        }
        return _findFieldPs(children, predicate);
    };

    var _destroy = function() {

        _fieldPs.forEach(function(_fieldP) {
            _fieldP.destroy();
        });

        view.destroy();
    };

    return{
        init: _init,
        destroy: _destroy,
        updateAffectedIds: function(query, ids) {
            _definitionId = emptyInstanceM.getDefinitionId();
            _query = query;
            _ids = ids;

            _countInstancesAffected(_definitionId, _query, _ids, function(totalAffected) {
                view.updateEditInGroupTotalAffected(totalAffected);
            });
        }
    }
};

export default GroupEditInstanceP;
